import { Component, Inject, OnInit, Optional } from '@angular/core';
import { ListButton } from '@app/core/components/list/list.component';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { PoFinishSelectionGet, ProductionOrderSelectionItem, ProductionOrderService } from '@app/services/production-order.service';

@Component({
    selector: 'tt-select-po-finish',
    templateUrl: './select-po-finish.component.html',
    styleUrls: ['./select-po-finish.component.css'],
    standalone: false
})
export class SelectPoFinishComponent implements OnInit {
    public page: PoFinishSelectionGet = { filtervalue: '' };

    public openOption: 'selectpofinish_open' | 'selectpofinish_notopen' = 'selectpofinish_open';

    public finishOption: 'selectpofinish_open_mine' | 'selectpofinish_open_other' = 'selectpofinish_open_mine';

    public finishedOrders: ProductionOrderSelectionItem[] = [];

    public productionOrders: ProductionOrderSelectionItem[] = [];

    public error = '';

    private webpageName = '';

    constructor(
        private production: ProductionOrderService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public async ngOnInit(): Promise<void> {
        this.webpageName = String(this.navigator?.params['webpagename'] ?? '');

        try {
            this.page = await this.production.getSelectionSettings({ webpagename: this.webpageName });

            if (this.openOption === 'selectpofinish_open') await this.loadFinishedOrders();
        } catch (error) {
            this.showError(error);
        }
    }

    public async changeOpenOption(value: string): Promise<void> {
        if (value !== 'selectpofinish_open' && value !== 'selectpofinish_notopen') return;

        this.openOption = value;

        if (value === 'selectpofinish_notopen') await this.loadProductionOrders();
        else await this.loadFinishedOrders();
    }

    public async changeFinishOption(value: string): Promise<void> {
        if (value !== 'selectpofinish_open_mine' && value !== 'selectpofinish_open_other') return;

        this.finishOption = value;

        await this.loadFinishedOrders();
    }

    private async loadFinishedOrders(): Promise<void> {
        try { this.finishedOrders = await this.production.listFinishedOrders({ select_type: this.finishOption }); }
        catch (error) { this.showError(error); }
    }
    private async loadProductionOrders(): Promise<void> {
        const { stockplace_id, operation_id } = this.page;

        if (typeof stockplace_id !== 'string' || stockplace_id.trim() === '' || typeof operation_id !== 'string' || operation_id.trim() === '') return;

        try { this.productionOrders = await this.production.listProductionOrders({ stockplace_id, operation_id }); }
        catch (error) { this.showError(error); }
    }

    public get filteredOrders(): ProductionOrderSelectionItem[] {
        const items = this.openOption === 'selectpofinish_open' ? this.finishedOrders : this.productionOrders;
        const search = this.page.filtervalue ?? '';

        if (search === '') return items;

        const words = search.toLowerCase().split(' ');

        return items.filter((item, index) => items.indexOf(item) === index && (
            item.item_filtervalue === undefined || item.item_id === '0' || item.item_id === '-1' ||
            words.every(word => item.item_filtervalue!.toLowerCase().includes(word))
        ));
    }

    public select(item: ProductionOrderSelectionItem): void {
        if (item.item_id === '') return;

        // The legacy list returns immediately; scanning and saving continue after navigation.
        if (this.openOption === 'selectpofinish_open') {
            void this.production.selectFinishedOrder({
                p2_pofinish_keyno: item.item_id,
                webpagename: this.webpageName
            }).catch(error => this.showError(error));
        } else {
            void this.scanAndSelect(item).catch(error => this.showError(error));
        }

        this.navigator?.back();
    }

    private async scanAndSelect(item: ProductionOrderSelectionItem): Promise<void> {
        const scan = (await this.production.scanProductionOrder({ barcode: item.barcode }))[0];

        if (Number(scan?.p2_pofinish_keyno) > 0) {
            await this.production.selectFinishedOrder({
                p2_pofinish_keyno: scan.p2_pofinish_keyno,
                webpagename: this.webpageName
            });
        }
    }

    private showError(error: unknown): void { this.error = error instanceof Error ? error.message : String(error); }
}
